import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibraryOneOnOneParams } from '../../../services/api/dataLibrary';
import type {
  DataLibraryOneOnOneRow,
  DataLibraryOneOnOneFilterOptions,
  DataLibraryOneOnOneSummary,
} from '../../../types';
import { ONE_ON_ONE_TYPE_LABELS } from '../../../types';
import type { SortConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { SortState } from '../../../utils/sortTasks';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, type GroupNode } from '../../../utils/groupDataByField';
import { ONE_ON_ONE_COLUMNS, ONE_ON_ONE_STATUS_OPTIONS, ABSENT_REASON_LABELS } from './oneOnOneColumns';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

/** A row with its codes turned into labels, so cells and Group By read the same text. */
export type OneOnOneDisplayRow = Omit<DataLibraryOneOnOneRow, 'status' | 'one_on_one_type' | 'absent_reason'> & {
  status: string;
  one_on_one_type: string | null;
  absent_reason: string | null;
};

const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  ONE_ON_ONE_STATUS_OPTIONS.map(o => [o.value, o.label]),
);

export function useDataLibraryOneOnOnes({ selectedCohortIds }: { selectedCohortIds: string[] }) {
  const [data, setData] = useState<DataLibraryOneOnOneRow[]>([]);
  const [summary, setSummary] = useState<DataLibraryOneOnOneSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibraryOneOnOneFilterOptions>({
    cohorts: [],
    coaches: [],
    types: [],
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({ key: 'scheduled_start', order: 'DESC' });
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>([]);
  const [activeGroup, setActiveGroup] = useState<GroupState | null>(null);
  const { columns, setColumns } = useDataLibraryColumns(ONE_ON_ONE_COLUMNS);
  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  // Filters only; the export modal reuses these without paging.
  const filterParams = useMemo(() => {
    const params: Omit<DataLibraryOneOnOneParams, 'cohortIds'> = {};
    if (debouncedSearch) params.search = debouncedSearch;
    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      if (filter.key === 'cohort') params.cohortId = filter.values[0];
      if (filter.key === 'coach') params.coachId = filter.values[0];
      if (filter.key === 'status') params.status = filter.values.join(',');
      if (filter.key === 'type') params.type = filter.values.join(',');
    }
    return params;
  }, [debouncedSearch, activeFilters]);

  // MDF-39: the filtered total when it is over MAX_GROUPED_ROWS; the table then stays paged
  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  // MDF-39: only the newest request may write state; a grouped load spans many requests
  const latestRequest = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (selectedCohortIds.length === 0) {
      setData([]);
      setSummary(null);
      setTotalCount(0);
      setTotalPages(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = {
        cohortIds: selectedCohortIds,
        ...(sortConfig && { sortBy: sortConfig.key, sortOrder: sortConfig.order }),
        ...filterParams,
      };
      const fetchPage = (p: number, limit: number) =>
        dataLibraryApi.getOneOnOnes({ ...params, page: p, limit }).then(r => ({ ...r, meta: { pagination: r.pagination } }));
      // MDF-39: a grouped table needs every row, so it fetches all pages
      const result = isGrouped
        ? await fetchAllPages(fetchPage, () => requestId !== latestRequest.current)
        : await fetchPage(page, pageSize);
      if (requestId !== latestRequest.current) return;
      const total = result.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setData(result.data || []);
      setSummary(result.summary);
      setTotalCount(result.pagination.total);
      setTotalPages(result.pagination.totalPages);
      setFilterOptions(result.filterOptions);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({ title: 'Error', message: 'Failed to load 1:1 data', color: 'red' });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, page, pageSize, sortConfig, filterParams, isGrouped]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilters, selectedCohortIds, activeGroup]);

  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({ key, order: prev?.key === key && prev.order === 'ASC' ? 'DESC' : 'ASC' }));
  }, []);

  const handleSortPanelChange = useCallback((sort: SortState | null) => {
    setSortConfig(sort ? { key: sort.field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' } : null);
  }, []);

  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => (prev.some(f => f.key === key) ? prev : [...prev, { key, values: [] }]));
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters = activeFilters.some(f => f.values.length > 0) || debouncedSearch.length > 0;

  const rows: OneOnOneDisplayRow[] = useMemo(
    () =>
      data.map(row => ({
        ...row,
        status: STATUS_LABELS[row.status] ?? row.status,
        one_on_one_type: row.one_on_one_type ? ONE_ON_ONE_TYPE_LABELS[row.one_on_one_type] ?? row.one_on_one_type : null,
        absent_reason: row.absent_reason ? ABSENT_REASON_LABELS[row.absent_reason] ?? row.absent_reason : null,
      })),
    [data],
  );

  const groupedRows = useMemo(
    () =>
      groupDataByLevels(
        rows as unknown as Record<string, unknown>[],
        isGrouped ? activeGroup : null,
        columns,
      ) as GroupNode<OneOnOneDisplayRow>[] | null,
    [rows, activeGroup, isGrouped, columns],
  );

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    rows,
    groupedRows,
    summary,
    loading,
    totalCount,
    totalPages,
    filterOptions,
    filterParams,
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    activeGroup,
    setActiveGroup,
    columns,
    setColumns,
    visibleColumns,
  };
}
